import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import './Navigation.css';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { isAuthenticated, clearAuthData } from '../utils/auth';
import { coreApi, authApi } from '../api/api';
import { auth } from '../firebase/config';
import { useWorkspace } from '../context/WorkspaceContext';
import {
    getJobPositionCurrentRoute,
    hasValidStages,
    getOrderedStages,
} from '../utils/jobPositionNavigation';

/* ------------------------------------------------------------------ */
/* Icons                                                               */
/* ------------------------------------------------------------------ */

const Icon = ({ children, size = 18 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
    >
        {children}
    </svg>
);

const icons = {
    home: (size) => (
        <Icon size={size}>
            <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </Icon>
    ),
    briefcase: (size) => (
        <Icon size={size}>
            <rect x="2" y="7" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
            <path d="M16 21V5C16 3.89543 15.1046 3 14 3H10C8.89543 3 8 3.89543 8 5V21" stroke="currentColor" strokeWidth="2"/>
        </Icon>
    ),
    chart: (size) => (
        <Icon size={size}>
            <path d="M18 20V10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M12 20V4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M6 20V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </Icon>
    ),
    grid: (size) => (
        <Icon size={size}>
            <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <rect x="14" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </Icon>
    ),
    calendar: (size) => (
        <Icon size={size}>
            <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
            <path d="M16 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M8 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M3 10H21" stroke="currentColor" strokeWidth="2"/>
        </Icon>
    ),
    target: (size) => (
        <Icon size={size}>
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
            <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2"/>
        </Icon>
    ),
    alert: (size) => (
        <Icon size={size}>
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="12" cy="16" r="1" fill="currentColor"/>
        </Icon>
    ),
    users: (size) => (
        <Icon size={size}>
            <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
            <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </Icon>
    ),
    fileText: (size) => (
        <Icon size={size}>
            <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </Icon>
    ),
    mail: (size) => (
        <Icon size={size}>
            <rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
            <path d="M22 6L12 13L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </Icon>
    ),
    inbox: (size) => (
        <Icon size={size}>
            <path d="M22 12H16L14 15H10L8 12H2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M5.45 5.11L2 12V18C2 18.5304 2.21071 19.0391 2.58579 19.4142C2.96086 19.7893 3.46957 20 4 20H20C20.5304 20 21.0391 19.7893 21.4142 19.4142C21.7893 19.0391 22 18.5304 22 18V12L18.55 5.11C18.3844 4.77679 18.1292 4.49637 17.813 4.30028C17.4967 4.10419 17.1321 4.0002 16.76 4H7.24C6.86792 4.0002 6.50326 4.10419 6.18704 4.30028C5.87083 4.49637 5.61558 4.77679 5.45 5.11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </Icon>
    ),
    settings: (size) => (
        <Icon size={size}>
            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
            <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </Icon>
    ),
    user: (size) => (
        <Icon size={size}>
            <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </Icon>
    ),
    logout: (size) => (
        <Icon size={size}>
            <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </Icon>
    ),
    login: (size) => (
        <Icon size={size}>
            <path d="M15 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M10 17L15 12L10 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M15 12H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </Icon>
    ),
    chevronDown: (size) => (
        <Icon size={size}>
            <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </Icon>
    ),
    chevronLeft: (size) => (
        <Icon size={size}>
            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </Icon>
    ),
    check: (size) => (
        <Icon size={size}>
            <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
        </Icon>
    ),
    search: (size) => (
        <Icon size={size}>
            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
            <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </Icon>
    ),
    menu: (size) => (
        <Icon size={size}>
            <path d="M3 6H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M3 12H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </Icon>
    ),
};

/* ------------------------------------------------------------------ */
/* URL parsing                                                         */
/* ------------------------------------------------------------------ */

const ANALYTICS_SECTIONS = new Set([
    'interview-analytics',
    'motivation-assessment',
    'risk-assessment',
    'compare',
]);

const MANAGEMENT_SECTIONS = new Set([
    'applications',
    'application',
    'work-trials',
    'work-trial',
    'interviews',
    'inbox',
    'email-management',
    'settings',
]);

// Detail pages highlight their parent list item in the sidebar
const SECTION_ALIASES = {
    application: 'applications',
    'work-trial': 'work-trials',
};

const parsePositionLocation = (pathname) => {
    const match = pathname.match(/^\/job-position\/([^/]+)(?:\/([^/]+))?(?:\/([^/]+))?/);
    if (!match) {
        return { jobPositionId: null, activeTab: null, activeSubTab: null };
    }
    const [, jobPositionId, section = '', third = ''] = match;
    let activeTab = 'hire';
    let activeSubTab = section;
    if (ANALYTICS_SECTIONS.has(section)) {
        activeTab = 'analytics';
    } else if (MANAGEMENT_SECTIONS.has(section)) {
        activeTab = 'management';
        activeSubTab = SECTION_ALIASES[section] || section;
    } else if (section === 'stage') {
        activeSubTab = third;
    }
    return { jobPositionId, activeTab, activeSubTab };
};

/* ------------------------------------------------------------------ */
/* Section item builders                                               */
/* ------------------------------------------------------------------ */

const buildHireItems = (jobPositionId, jobPosition) => {
    const base = `/job-position/${jobPositionId}`;
    const items = [
        {
            id: 'job-posting',
            label: 'Job Posting',
            path: `${base}/job-posting`,
            completed: jobPosition ? (jobPosition.has_job_posting ?? true) : null,
        },
        { id: 'sourcing', label: 'Sourcing', path: `${base}/sourcing`, completed: null },
    ];

    if (hasValidStages(jobPosition)) {
        getOrderedStages(jobPosition).forEach((stage) => {
            items.push({
                id: stage.slug,
                label: stage.stage_name,
                path: `${base}/stage/${stage.slug}`,
                completed: stage.selection_completed === true,
            });
        });
    } else {
        const includeInterviews = jobPosition?.include_interviews === true || jobPosition?.include_interviews === 'true';
        const includeWorkTrial = jobPosition?.include_work_trial === true || jobPosition?.include_work_trial === 'true' || jobPosition?.include_work_trial === undefined;

        items.push({
            id: 'resume-screening',
            label: 'Resume Screening',
            path: `${base}/resume-screening`,
            completed: jobPosition?.resume_screening_selection_completed === true,
        });
        if (includeInterviews) {
            items.push({
                id: 'interview-screening',
                label: 'Interview Screening',
                path: `${base}/interview-screening`,
                completed: jobPosition?.interview_screening_selection_completed === true,
            });
        }
        if (includeWorkTrial) {
            items.push({
                id: 'work-trial-screening',
                label: 'Work Trial Screening',
                path: `${base}/work-trial-screening`,
                completed: jobPosition?.work_trial_selection_completed === true,
            });
        }
        items.push({ id: 'hire', label: 'Hire', path: `${base}/hire`, completed: null });
    }

    return items;
};

const buildAnalyticsItems = (jobPositionId) => {
    const base = `/job-position/${jobPositionId}`;
    return [
        { id: 'interview-analytics', label: 'Interview Analytics', path: `${base}/interview-analytics`, icon: 'calendar' },
        { id: 'motivation-assessment', label: 'Motivation Assessment', path: `${base}/motivation-assessment`, icon: 'target' },
        { id: 'risk-assessment', label: 'Risk Assessment', path: `${base}/risk-assessment`, icon: 'alert' },
        { id: 'compare', label: 'Compare', path: `${base}/compare`, icon: 'grid' },
    ];
};

const buildManagementItems = (jobPositionId) => {
    const base = `/job-position/${jobPositionId}`;
    return [
        { id: 'applications', label: 'Applications', path: `${base}/applications`, icon: 'users' },
        { id: 'work-trials', label: 'Work Trials', path: `${base}/work-trials`, icon: 'fileText' },
        { id: 'interviews', label: 'Interviews', path: `${base}/interviews`, icon: 'calendar' },
        { id: 'email-management', label: 'Emails', path: `${base}/email-management`, icon: 'mail' },
        { id: 'inbox', label: 'Inbox', path: `${base}/inbox`, icon: 'inbox' },
        { id: 'settings', label: 'Settings', path: `${base}/settings`, icon: 'settings' },
    ];
};

const MAIN_TABS = [
    { id: 'hire', label: 'Hire', icon: 'briefcase' },
    { id: 'analytics', label: 'Analytics', icon: 'chart' },
    { id: 'management', label: 'Manage', icon: 'grid' },
];

const SECTION_LABELS = {
    hire: 'Hiring Pipeline',
    analytics: 'Reports',
    management: 'Management',
};

/* ------------------------------------------------------------------ */
/* Navigation                                                          */
/* ------------------------------------------------------------------ */

// Routes that render chrome-free (no sidebar, no hamburger), matching their
// pre-redesign look
const CHROME_FREE_ROUTES = new Set(['/privacy-policy']);

const Navigation = ({ __previewState = null }) => {
    const location = useLocation();
    // On the dev-only /nav-preview playground the page supplies its own
    // <Navigation __previewState={...}>; the global instance steps aside and
    // must not render or write global side effects (html attr / localStorage).
    const isPreviewRoute = process.env.NODE_ENV === 'development' && location.pathname === '/nav-preview';
    const stepAside = isPreviewRoute && !__previewState;
    const navigate = useNavigate();
    const { title: workspaceTitle, positionNav } = useWorkspace();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(() => {
        try {
            return localStorage.getItem('unifiedNavCollapsed') === '1';
        } catch {
            return false;
        }
    });
    const [dropdownOpen, setDropdownOpen] = useState(() => !!(__previewState && __previewState.dropdownOpen));
    const [positions, setPositions] = useState(() => __previewState?.positions || null); // null = never fetched
    const [positionsLoading, setPositionsLoading] = useState(false);
    const [positionQuery, setPositionQuery] = useState('');
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    /* ---------- auth state ---------- */
    useEffect(() => {
        const checkAuthStatus = () => setIsLoggedIn(isAuthenticated());
        checkAuthStatus();
        window.addEventListener('authStateChange', checkAuthStatus);
        return () => window.removeEventListener('authStateChange', checkAuthStatus);
    }, [location]);

    /* ---------- close transient UI on route change ---------- */
    const prevPathRef = useRef(location.pathname);
    useEffect(() => {
        if (prevPathRef.current === location.pathname) return;
        prevPathRef.current = location.pathname;
        setIsMenuOpen(false);
        setDropdownOpen(false);
    }, [location.pathname]);

    /* ---------- collapse: expose to page CSS via <html> attribute ---------- */
    const wasSteppedAsideRef = useRef(stepAside);
    useEffect(() => {
        if (stepAside) {
            wasSteppedAsideRef.current = true;
            return;
        }
        if (wasSteppedAsideRef.current) {
            // Another instance (the dev preview) may have toggled collapse
            // while this one was inactive — resync from localStorage first
            wasSteppedAsideRef.current = false;
            let stored = false;
            try {
                stored = localStorage.getItem('unifiedNavCollapsed') === '1';
            } catch {
                stored = collapsed;
            }
            if (stored !== collapsed) {
                setCollapsed(stored);
                return; // effect re-runs with the synced value
            }
        }
        const rootEl = document.documentElement;
        if (collapsed) {
            rootEl.setAttribute('data-nav-collapsed', '');
        } else {
            rootEl.removeAttribute('data-nav-collapsed');
        }
        try {
            localStorage.setItem('unifiedNavCollapsed', collapsed ? '1' : '0');
        } catch {
            // localStorage unavailable — collapse simply won't persist
        }
    }, [collapsed, stepAside]);

    /* ---------- derive nav state from URL + published page data ---------- */
    const parsed = parsePositionLocation(location.pathname);
    // Preview sentinel { none: true } means "render the plain no-position mode"
    const preview = __previewState && !__previewState.none ? __previewState : null;
    const jobPositionId = preview ? preview.jobPositionId : parsed.jobPositionId;
    const activeTab = preview ? preview.activeTab : parsed.activeTab;
    const activeSubTab = preview ? preview.activeSubTab : parsed.activeSubTab;

    const navData = preview || (positionNav && positionNav.jobPositionId === jobPositionId ? positionNav : null);
    const jobPosition = navData?.jobPosition || null;
    const positionLoading = !jobPosition && (!navData || navData.isLoading !== false);

    const positionTitle = jobPosition?.title || (preview ? null : workspaceTitle) || null;
    const hasJobPosting = jobPosition?.has_job_posting ?? true;

    const hireItems = useMemo(
        () => (jobPositionId ? buildHireItems(jobPositionId, jobPosition) : []),
        [jobPositionId, jobPosition]
    );
    const analyticsItems = useMemo(
        () => (jobPositionId ? buildAnalyticsItems(jobPositionId) : []),
        [jobPositionId]
    );
    const managementItems = useMemo(
        () => (jobPositionId ? buildManagementItems(jobPositionId) : []),
        [jobPositionId]
    );

    const mainTabPaths = useMemo(() => {
        if (!jobPositionId) return {};
        let hirePath = '';
        if (jobPosition) {
            hirePath = getJobPositionCurrentRoute(jobPosition, hasJobPosting);
        }
        if (!hirePath || hirePath === '/') {
            hirePath = `/job-position/${jobPositionId}/job-posting`;
        }
        return {
            hire: hirePath,
            analytics: `/job-position/${jobPositionId}/interview-analytics`,
            management: `/job-position/${jobPositionId}/applications`,
        };
    }, [jobPositionId, jobPosition, hasJobPosting]);

    const activeTabIndex = Math.max(0, MAIN_TABS.findIndex((t) => t.id === activeTab));

    /* ---------- position switcher ---------- */
    const fetchPositions = useCallback(async () => {
        setPositionsLoading(true);
        try {
            let uid = auth.currentUser?.uid;
            if (!uid) {
                // Firebase may still be restoring the session after a hard
                // reload — wait for the first auth state emission
                uid = await new Promise((resolve) => {
                    const unsubscribe = auth.onAuthStateChanged((user) => {
                        unsubscribe();
                        resolve(user?.uid || null);
                    });
                });
            }
            if (!uid) {
                setPositions((prev) => prev || []);
                return;
            }
            const response = await authApi.get(`/job-positions/${uid}/`);
            setPositions(response.data['job-positions'] || []);
        } catch {
            setPositions((prev) => prev || []);
        } finally {
            setPositionsLoading(false);
        }
    }, []);

    // Refresh the switcher list whenever the dropdown opens; the cached
    // list stays visible while the refresh happens (skeleton on first open)
    useEffect(() => {
        if (dropdownOpen) fetchPositions();
    }, [dropdownOpen, fetchPositions]);

    // Search only shows for lists long enough to need it
    const searchable = !!(positions && positions.length > 6);

    // Reset the query when the dropdown closes; focus the input when it opens
    useEffect(() => {
        if (!dropdownOpen) {
            setPositionQuery('');
        } else if (searchable) {
            searchInputRef.current?.focus();
        }
    }, [dropdownOpen, searchable]);

    const filteredPositions = useMemo(() => {
        if (!positions) return positions;
        const q = positionQuery.trim().toLowerCase();
        if (!q) return positions;
        return positions.filter((p) =>
            p.title?.toLowerCase().includes(q) ||
            p.department?.toLowerCase().includes(q) ||
            p.location?.toLowerCase().includes(q)
        );
    }, [positions, positionQuery]);

    const toggleDropdown = () => {
        if (collapsed) {
            setCollapsed(false);
            return;
        }
        setDropdownOpen(!dropdownOpen);
    };

    /* ---------- close dropdown on outside click / Escape ---------- */
    useEffect(() => {
        if (!dropdownOpen) return;
        const onPointerDown = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        const onKeyDown = (e) => {
            if (e.key === 'Escape') setDropdownOpen(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [dropdownOpen]);

    const switchToPosition = (position) => {
        setDropdownOpen(false);
        if (position.job_position_id === jobPositionId) return;
        const base = `/job-position/${position.job_position_id}`;
        let target;
        if (activeTab === 'analytics' && ANALYTICS_SECTIONS.has(activeSubTab)) {
            // Analytics routes are identical across positions — keep the view
            target = `${base}/${activeSubTab}`;
        } else if (activeTab === 'management' && MANAGEMENT_SECTIONS.has(activeSubTab)) {
            // Same for management list routes
            target = `${base}/${activeSubTab}`;
        } else if (activeSubTab === 'job-posting' || activeSubTab === 'sourcing') {
            target = `${base}/${activeSubTab}`;
        } else {
            // Pipeline stages differ per position — land on its current stage
            target = getJobPositionCurrentRoute(position, position.has_job_posting ?? true);
            if (!target || target === '/') target = `${base}/job-posting`;
        }
        navigate(target);
    };

    /* ---------- auth actions ---------- */
    const handleLogout = async () => {
        try {
            await coreApi.post('/auth/logout');
        } catch {
            // Even if the server call fails, clear local state
        }
        clearAuthData();
        window.location.href = '/login';
    };

    const handleLogin = () => {
        const returnUrl = encodeURIComponent(location.pathname);
        navigate(`/login?returnTo=${returnUrl}`);
    };

    const closeMenu = () => setIsMenuOpen(false);

    // After all hooks: step aside on the preview route, and stay hidden on
    // chrome-free routes that never had navigation
    if (stepAside || CHROME_FREE_ROUTES.has(location.pathname)) return null;

    /* ---------- renderers ---------- */

    const renderTimeline = () => {
        if (positionLoading) {
            return (
                <div className="unav-steps" aria-hidden="true">
                    {[0, 1, 2, 3, 4].map((i) => (
                        <div key={i} className="unav-step unav-step-skeleton" style={{ '--i': i }}>
                            <span className="unav-step-indicator" />
                            <span className="unav-skeleton unav-skeleton-line" style={{ width: `${62 + (i % 3) * 22}%` }} />
                        </div>
                    ))}
                </div>
            );
        }
        const activeIdx = hireItems.findIndex((item) => item.id === activeSubTab);
        return (
            <div className="unav-steps">
                {hireItems.map((item, i) => {
                    const isActive = item.id === activeSubTab;
                    const isDone = !isActive && (item.completed ?? (activeIdx >= 0 && i < activeIdx));
                    const state = isActive ? 'active' : isDone ? 'done' : 'todo';
                    return (
                        <Link
                            key={item.id}
                            to={item.path}
                            className={`unav-step ${state}`}
                            style={{ '--i': i }}
                            onClick={closeMenu}
                            aria-current={isActive ? 'page' : undefined}
                        >
                            <span className="unav-step-indicator">
                                {state === 'done' && icons.check(11)}
                            </span>
                            <span className="unav-step-label">{item.label}</span>
                        </Link>
                    );
                })}
            </div>
        );
    };

    const renderIconList = (items) => (
        <div className="unav-rows">
            {items.map((item, i) => {
                const isActive = item.id === activeSubTab;
                return (
                    <Link
                        key={item.id}
                        to={item.path}
                        className={`unav-row ${isActive ? 'active' : ''}`}
                        style={{ '--i': i }}
                        onClick={closeMenu}
                        aria-current={isActive ? 'page' : undefined}
                    >
                        <span className="unav-row-icon">{icons[item.icon](17)}</span>
                        <span className="unav-row-label">{item.label}</span>
                    </Link>
                );
            })}
        </div>
    );

    const positionInitial = (positionTitle || '•').trim().charAt(0).toUpperCase();

    return (
        <>
            {/* Mobile hamburger */}
            <button
                className="unav-hamburger"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle navigation menu"
                aria-expanded={isMenuOpen}
            >
                {icons.menu(22)}
            </button>

            {/* Mobile backdrop */}
            <div
                className={`unav-backdrop ${isMenuOpen ? 'visible' : ''}`}
                onClick={closeMenu}
                aria-hidden="true"
            />

            <aside className={`unav ${isMenuOpen ? 'open' : ''} ${collapsed ? 'collapsed' : ''}`}>
                {/* Brand */}
                <div className="unav-header">
                    <Link to="/" className="unav-brand" onClick={closeMenu}>
                        <img src="/images/appli-logo-white.png" alt="Appli logo" className="unav-brand-mark" />
                        <span className="unav-brand-name">Appli</span>
                    </Link>
                    <button
                        className="unav-collapse-btn"
                        onClick={() => setCollapsed(!collapsed)}
                        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        {icons.chevronLeft(16)}
                    </button>
                </div>

                <div className="unav-scroll">
                    {/* Primary */}
                    <nav className="unav-primary">
                        <Link
                            to="/"
                            className={`unav-item ${location.pathname === '/' ? 'active' : ''}`}
                            onClick={closeMenu}
                            title="Dashboard"
                        >
                            <span className="unav-item-icon">{icons.home(18)}</span>
                            <span className="unav-item-label">Dashboard</span>
                        </Link>
                    </nav>

                    {/* Job position context */}
                    {jobPositionId && (
                        <div className="unav-position">
                            <div className="unav-divider" />

                            {/* Position selector card */}
                            <div className="unav-poscard-wrap" ref={dropdownRef}>
                                <button
                                    className={`unav-poscard ${dropdownOpen ? 'open' : ''}`}
                                    onClick={toggleDropdown}
                                    aria-haspopup="listbox"
                                    aria-expanded={dropdownOpen}
                                    title={positionTitle || 'Job position'}
                                >
                                    <span className="unav-poscard-avatar">{positionInitial}</span>
                                    <span className="unav-poscard-info">
                                        <span className="unav-poscard-eyebrow">Job Position</span>
                                        {positionTitle ? (
                                            <span className="unav-poscard-title">{positionTitle}</span>
                                        ) : (
                                            <span className="unav-skeleton unav-skeleton-title" />
                                        )}
                                    </span>
                                    {jobPosition && hasJobPosting && <span className="unav-poscard-dot" />}
                                    <span className="unav-poscard-chevron">{icons.chevronDown(16)}</span>
                                </button>

                                {/* Switcher dropdown */}
                                <div className={`unav-posmenu ${dropdownOpen ? 'open' : ''}`} role="listbox">
                                    {searchable && (
                                        <div className="unav-posmenu-search">
                                            {icons.search(14)}
                                            <input
                                                ref={searchInputRef}
                                                type="text"
                                                placeholder="Search positions…"
                                                value={positionQuery}
                                                onChange={(e) => setPositionQuery(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && filteredPositions?.length) {
                                                        switchToPosition(filteredPositions[0]);
                                                    }
                                                }}
                                                aria-label="Search positions"
                                            />
                                        </div>
                                    )}
                                    <div className="unav-posmenu-list">
                                        {positionsLoading && !positions && (
                                            <div className="unav-posmenu-loading">
                                                {[0, 1, 2].map((i) => (
                                                    <div key={i} className="unav-posmenu-item is-skeleton" style={{ '--i': i }}>
                                                        <span className="unav-posmenu-avatar unav-skeleton" />
                                                        <span className="unav-skeleton unav-skeleton-line" style={{ width: `${55 + i * 12}%` }} />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {filteredPositions && filteredPositions.length === 0 && (
                                            <div className="unav-posmenu-empty">
                                                {positionQuery ? 'No matching positions' : 'No positions found'}
                                            </div>
                                        )}
                                        {filteredPositions && filteredPositions.map((position) => {
                                            const isCurrent = position.job_position_id === jobPositionId;
                                            return (
                                                <button
                                                    key={position.job_position_id}
                                                    className={`unav-posmenu-item ${isCurrent ? 'current' : ''}`}
                                                    onClick={() => switchToPosition(position)}
                                                    role="option"
                                                    aria-selected={isCurrent}
                                                    title={position.title}
                                                >
                                                    <span className="unav-posmenu-avatar">
                                                        {(position.title || '•').trim().charAt(0).toUpperCase()}
                                                    </span>
                                                    <span className="unav-posmenu-title">{position.title}</span>
                                                    {isCurrent && <span className="unav-posmenu-check">{icons.check(13)}</span>}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <div className="unav-posmenu-footer">
                                        <Link to="/" onClick={() => { setDropdownOpen(false); closeMenu(); }}>
                                            {icons.grid(14)}
                                            <span>View all positions</span>
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            {/* Mode tabs */}
                            <div className="unav-tabs" style={{ '--tab-idx': activeTabIndex }}>
                                <span className="unav-tabs-pill" aria-hidden="true" />
                                {MAIN_TABS.map((tab) => (
                                    <Link
                                        key={tab.id}
                                        to={mainTabPaths[tab.id] || '#'}
                                        className={`unav-tab ${activeTab === tab.id ? 'active' : ''}`}
                                        onClick={closeMenu}
                                        title={tab.label}
                                        aria-current={activeTab === tab.id ? 'true' : undefined}
                                    >
                                        <span className="unav-tab-icon">{icons[tab.icon](17)}</span>
                                        <span className="unav-tab-label">{tab.label}</span>
                                    </Link>
                                ))}
                            </div>

                            {/* Contextual section — remounts on tab change for enter animation */}
                            <div className="unav-section" key={activeTab}>
                                <div className="unav-section-label">{SECTION_LABELS[activeTab]}</div>
                                {activeTab === 'hire' && renderTimeline()}
                                {activeTab === 'analytics' && renderIconList(analyticsItems)}
                                {activeTab === 'management' && renderIconList(managementItems)}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="unav-footer">
                    <Link
                        to="/account"
                        className={`unav-item ${location.pathname === '/account' ? 'active' : ''}`}
                        onClick={closeMenu}
                        title="Account"
                    >
                        <span className="unav-item-icon">{icons.user(18)}</span>
                        <span className="unav-item-label">Account</span>
                    </Link>
                    {isLoggedIn ? (
                        <button
                            className="unav-item unav-logout"
                            onClick={() => { handleLogout(); closeMenu(); }}
                            title="Logout"
                        >
                            <span className="unav-item-icon">{icons.logout(18)}</span>
                            <span className="unav-item-label">Logout</span>
                        </button>
                    ) : (
                        <button
                            className="unav-item unav-login"
                            onClick={() => { handleLogin(); closeMenu(); }}
                            title="Login"
                        >
                            <span className="unav-item-icon">{icons.login(18)}</span>
                            <span className="unav-item-label">Login</span>
                        </button>
                    )}
                </div>
            </aside>
        </>
    );
};

export default Navigation;
